import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button } from './ui';
import Convocatoria from './torneos/Convocatoria';
import AvisoDeportesFaltantes from './deportes/AvisoDeportesFaltantes';
import { usePlatformBranding } from '../hooks/usePlatformBranding';

// La convocatoria abierta con el enlace que el organizador comparte (WhatsApp, etc).
//
// Antes invitar exigía un correo, y ese correo tenía que ser EXACTAMENTE el de acceso
// del club: el organizador tenía que ATINARLE. Con este enlace la dirección se
// invierte — lo manda a quien se lo pida, y el club se identifica solo.
//
// Dos caminos desde la misma pantalla:
//   ya tiene cuenta -> un clic y queda con licencia (licencia_por_enlace)
//   no tiene cuenta -> el enlace acuña una invitación a su correo y de ahí sigue el
//                      registro que ya existía, sin tocarlo

const Shell = ({ branding, children }) => (
  <div className="min-h-screen bg-background px-4 py-10">
    <div className="mx-auto w-full max-w-lg">
      {branding?.logo_url && (
        <img src={branding.logo_url} alt="" className="mx-auto mb-6 h-10 object-contain" />
      )}
      <div className="rounded-xl border bg-card p-6 shadow-sm">{children}</div>
    </div>
  </div>
);

const ConvocatoriaPublica = () => {
  const navigate = useNavigate();
  const branding = usePlatformBranding();
  const token = new URLSearchParams(window.location.search).get('token') || '';

  const [convocatoria, setConvocatoria] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(null);
  const [sesion, setSesion] = useState(null);
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(null);

  // Datos del club que aún no tiene cuenta.
  const [modoRegistro, setModoRegistro] = useState(false);
  const [clubNombre, setClubNombre] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    let vivo = true;
    supabase.auth.getSession().then(({ data }) => { if (vivo) setSesion(data?.session || null); });
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    if (!token) { setCargando(false); setErrorCarga('Este enlace de convocatoria no es válido.'); return; }
    let vivo = true;
    supabase.rpc('get_convocatoria', { p_token: token }).then(({ data, error: err }) => {
      if (!vivo) return;
      setCargando(false);
      if (err) setErrorCarga(err.message || 'No pudimos abrir esta convocatoria.');
      else setConvocatoria(data);
    });
    return () => { vivo = false; };
  }, [token]);

  const participar = async () => {
    setError(null);
    setEnviando(true);
    const { data, error: err } = await supabase.rpc('licencia_por_enlace', { p_token: token });
    setEnviando(false);
    if (err) { setError(err.message || 'No se pudo registrar tu participación.'); return; }
    const fila = Array.isArray(data) ? data[0] : data;
    setListo(fila?.ya_tenia ? 'ya' : 'nuevo');
  };

  // Sin cuenta: se acuña la invitación con su correo y se sigue al registro de siempre.
  const registrar = async (e) => {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    const { data, error: err } = await supabase.rpc('invitacion_por_enlace', {
      p_token: token, p_email: email.trim(), p_club_nombre: clubNombre.trim(),
    });
    setEnviando(false);
    if (err) { setError(err.message || 'No se pudo continuar.'); return; }

    const fila = Array.isArray(data) ? data[0] : data;
    // Si ya había invitación para ese correo NO se entrega su token: esa credencial
    // solo vive en el buzón de su destinatario. Con el enlace compartido por WhatsApp,
    // devolverlo dejaría que cualquiera se apropiara de la invitación de otro club.
    if (!fila?.token) {
      setError(
        'Ya hay una invitación para ese correo. Búscala en tu bandeja de entrada, '
        + 'o pídele a la organizadora que te la reenvíe.'
      );
      return;
    }
    navigate(`/aceptar-invitacion?token=${encodeURIComponent(fila.token)}`);
  };

  if (cargando) return <Shell branding={branding}><p className="py-8 text-center text-sm text-muted-foreground">Cargando convocatoria...</p></Shell>;

  if (errorCarga) return (
    <Shell branding={branding}>
      <div className="text-center">
        <h1 className="mb-2 text-xl font-bold">Convocatoria</h1>
        <p className="mb-6 text-sm text-muted-foreground">{errorCarga}</p>
        <Button className="w-full" onClick={() => navigate('/login')}>Ir a MYKLUBB</Button>
      </div>
    </Shell>
  );

  if (listo) return (
    <Shell branding={branding}>
      <div className="text-center">
        <h1 className="mb-2 text-xl font-bold">
          {listo === 'ya' ? 'Tu club ya estaba dentro' : '¡Listo, tu club está dentro!'}
        </h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Ya puedes inscribir tus equipos en las categorías convocadas, desde cada grupo.
        </p>
        <Button className="w-full" onClick={() => navigate('/torneos')}>Ir a Torneos</Button>
      </div>
    </Shell>
  );

  return (
    <Shell branding={branding}>
      <Convocatoria convocatoria={convocatoria} />

      {error && (
        <div className="mt-5 rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="mt-6 border-t pt-5">
        {sesion ? (
          <>
            <AvisoDeportesFaltantes deportes={convocatoria?.deportes_faltantes} className="mb-4" />
            <Button className="w-full" onClick={participar} disabled={enviando}>
              {enviando ? 'Registrando...' : 'Participar en este torneo'}
            </Button>
          </>
        ) : modoRegistro ? (
          <form onSubmit={registrar} className="space-y-3">
            <p className="text-sm font-medium">Registra tu club</p>
            <div>
              <label className="mb-1 block text-sm">Nombre del club</label>
              <input
                value={clubNombre} onChange={(e) => setClubNombre(e.target.value)} required
                className="w-full rounded-md border border-input px-3 py-2" placeholder="Ej: Lobos FC"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm">Correo del administrador</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full rounded-md border border-input px-3 py-2" placeholder="tu@club.com"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Con este correo entrarás a MYKLUBB.
              </p>
            </div>
            <Button type="submit" className="w-full" disabled={enviando}>
              {enviando ? 'Continuando...' : 'Continuar'}
            </Button>
            <button
              type="button" onClick={() => setModoRegistro(false)}
              className="w-full text-xs text-muted-foreground hover:underline"
            >
              Volver
            </button>
          </form>
        ) : (
          <div className="space-y-2">
            <p className="mb-3 text-sm text-muted-foreground">
              Para participar, entra con tu cuenta de MYKLUBB o registra tu club.
            </p>
            <Button
              className="w-full"
              onClick={() => navigate(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
            >
              Ya tengo cuenta
            </Button>
            <Button variant="outline" className="w-full" onClick={() => setModoRegistro(true)}>
              Registrar mi club
            </Button>
          </div>
        )}
      </div>
    </Shell>
  );
};

export default ConvocatoriaPublica;
